import type { ReactText } from 'react';
import React, { useRef } from 'react';
import type { ModalProps } from '../ui/Modal/Modal';
import { Modal } from '../ui';
import type { SpotFormSaveType } from './SpotForm';
import SpotForm from './SpotForm';
import { useEditSpotModalQuery } from '../../graphql';
import {
  isPersistedSpotOrder,
  useUpdateSpotWithRelationsMutation,
} from './spot';
import { isDefined } from '../../lib/util';
import { toast } from 'react-toastify';
import { stringToNumOpt } from '../core/util';
import { useCurrentUser } from '../../state/zustand';

export interface EditSpotModalProps extends Omit<ModalProps, 'isOpen'> {
  spotId?: number;
  onEdit?: () => void;
}

const EditSpotModal = ({
  spotId,
  onClose,
  onEdit,
  ...restProps
}: EditSpotModalProps): JSX.Element | null => {
  const { data, refetch } = useEditSpotModalQuery({
    variables: { spotId: spotId ?? NaN },
    skip: !spotId,
  });
  const spot = data?.voyageCharterById ?? undefined;
  const [updateSpotWithRelations] = useUpdateSpotWithRelationsMutation();
  const currentUser = useCurrentUser();
  const saveToastId = useRef<ReactText | null>(null);

  const handleSave = async (data: SpotFormSaveType): Promise<unknown> => {
    const userId = currentUser?.id ?? null;

    if (!userId || !spot?.id) return;

    saveToastId.current = toast('Saving SPOT', {
      type: toast.TYPE.INFO,
    });

    try {
      await updateSpotWithRelations(
        { ...data.baseContract, id: spot.id },
        spot.orderRelations.nodes.filter(isDefined).map((rel) => {
          const freight = rel.freight.nodes[0];

          return {
            relationId: rel.id,
            orderId: rel.orderId,
            freightId: freight?.id ?? null,
            freightAmount: stringToNumOpt(freight?.freightAmount) ?? null,
            freightUnit: freight?.freightUnit ?? null,
          };
        }),
        data.relatedOrders.filter(isPersistedSpotOrder),
        data.portData ?? [],
        spot.portDataRelations.nodes
          .map((pd) => pd?.portData)
          .filter(isDefined),
        data.commissions,
        spot.commissionsByVoyageCharterId.nodes.filter(isDefined),
        data.extraFreights,
        spot.voyageCharterExtraFreightsByVoyageCharterId.nodes.filter(
          isDefined,
        ),
        true,
      );

      toast.update(saveToastId.current, {
        render: 'SPOT saved successfully',
        type: toast.TYPE.SUCCESS,
      });

      await refetch();
      onEdit?.();
      onClose?.();
    } catch (error) {
      console.error('Error saving SPOT', error);
      toast.update(saveToastId.current, {
        render: 'Error saving SPOT',
        type: toast.TYPE.ERROR,
      });
    }
  };

  if (!spotId) return null;
  return (
    <Modal isOpen onClose={onClose} {...restProps}>
      {spot && (
        <SpotForm spot={spot} onSave={handleSave} onClose={onClose} outgoing />
      )}
    </Modal>
  );
};

export default EditSpotModal;
